import React from "react"; // Importação necessária para JSX
import { toast } from "react-toastify";
import { i18n } from "../translate/i18n";
import { isString } from "lodash";

// Determina o tema atual dos toasts (light/dark) de forma resiliente
const getCurrentToastTheme = () => {
	try {
		const pref = window.localStorage.getItem("preferredTheme") || window.localStorage.getItem("themeChoice");
		return pref === "dark" ? "dark" : "light";
	} catch {
		return "light";
	}
};

// Componente visual do toast melhorado
const ErrorToastContent = ({ icon, title, message, actionButton }) => {
	const theme = getCurrentToastTheme();
	const titleColor = theme === "dark" ? "#fca5a5" : "#991b1b";
	const textColor = theme === "dark" ? "#fecaca" : "#7f1d1d";
	return (
		<div style={{
			display: "flex",
			alignItems: "flex-start",
			gap: "12px",
			textAlign: "left",
		}}>
			<span
				style={{
					fontSize: "20px",
					flexShrink: 0,
					marginTop: "2px",
				}}
			>
				{icon}
			</span>
			<div style={{ flex: 1 }}>
				{title && (
					<div
						style={{
							fontWeight: 600,
							marginBottom: "6px",
							fontSize: "15px",
							color: titleColor,
						}}
					>
						{title}
					</div>
				)}
				<div
					style={{
						fontSize: "14px",
						lineHeight: "1.4",
						color: textColor,
						marginBottom: actionButton ? "12px" : "0",
					}}
				>
					{message}
				</div>
				{actionButton && <div>{actionButton}</div>}
			</div>
		</div>
	);
};

// Função para criar botões de ação
const createActionButton = (text, onClick, isPrimary = true) => (
	<button 
		onClick={onClick}
		style={{
			background: isPrimary ? '#ef4444' : '#6b7280',
			color: 'white',
			border: 'none',
			padding: '8px 16px',
			borderRadius: '6px',
			fontSize: '13px',
			fontWeight: '500',
			cursor: 'pointer',
			transition: 'background 0.2s',
			marginRight: '8px'
		}}
		onMouseOver={(e) => e.target.style.background = isPrimary ? '#dc2626' : '#4b5563'}
		onMouseOut={(e) => e.target.style.background = isPrimary ? '#ef4444' : '#6b7280'}
	>
		{text}
	</button>
);

const toastError = (err) => {
	const errorMsg = err.response?.data?.error;
	const statusCode = err.response?.status;
	const theme = getCurrentToastTheme();

	// Configurações básicas melhoradas
	const getToastOptions = (autoCloseTime = 8000, critical = false) => ({
		autoClose: critical ? false : autoCloseTime,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
		theme,
		bodyStyle: {
			margin: 0,
			padding: 0,
			fontSize: '14px'
		},
		style: {
			borderRadius: "8px",
			boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
			backgroundColor: theme === 'dark' ? '#1f2937' : '#fef2f2',
			borderLeft: "4px solid #ef4444",
			padding: "16px",
			minHeight: "70px",
		},
		progressStyle: {
			background: "#ef4444",
			height: "3px",
		},
	});

	// Detectar tipo de erro e criar conteúdo apropriado
	let content, toastId, toastOptions;

	// Erro de rede/conexão
	if (!err.response || err.code === 'NETWORK_ERROR') {
		content = (
			<ErrorToastContent
				icon="🌐"
				title="Problema de Conexão"
				message="Verifique sua conexão com a internet e tente novamente"
				actionButton={createActionButton(
					"Tentar Novamente", 
					() => window.location.reload()
				)}
			/>
		);
		toastId = 'network-error';
		toastOptions = getToastOptions(10000); // 10 segundos
	}
		// Erro de autenticação
		else if (statusCode === 401 || statusCode === 403) {
			// Se estiver na tela de login, não exibir toast para erros 401/402
			try {
				const onLoginPage = typeof window !== 'undefined' && window.location && window.location.pathname === '/login';
				if (onLoginPage && (statusCode === 401 || statusCode === 402)) {
					return; // silencioso: a tela de login fará o tratamento inline
				}
			} catch (_) {}
		// Verificar se é erro de login (senha/email errado) ou sessão expirada
		const isLoginError = errorMsg && (
			errorMsg.toLowerCase().includes('password') ||
			errorMsg.toLowerCase().includes('senha') ||
			errorMsg.toLowerCase().includes('email') ||
			errorMsg.toLowerCase().includes('credentials') ||
			errorMsg.toLowerCase().includes('invalid') ||
			errorMsg.toLowerCase().includes('incorrect') ||
			window.location.pathname === '/login'
		);

			if (isLoginError) {
			// Erro de login (senha/email incorretos)
			const translatedMsg = errorMsg && i18n.exists(`backendErrors.${errorMsg}`) 
				? i18n.t(`backendErrors.${errorMsg}`) 
				: "Email ou senha incorretos. Verifique seus dados e tente novamente";

			content = (
				<ErrorToastContent
					icon="🔑"
					title="Dados de Login Incorretos"
					message={translatedMsg}
				/>
			);
			toastId = 'login-error';
			toastOptions = getToastOptions(6000); // Fecha em 6 segundos
			} else {
			// Sessão expirada (usuário já estava logado)
			content = (
				<ErrorToastContent
					icon="🔒"
					title="Sessão Expirada"
					message="Sua sessão expirou. Faça login novamente para continuar"
					actionButton={createActionButton(
						"Fazer Login", 
						() => window.location.href = '/login'
					)}
				/>
			);
			toastId = 'session-expired';
			toastOptions = getToastOptions(0, true); // Não fecha automaticamente
		}
	}
	// Erro do servidor (500+)
	else if (statusCode >= 500) {
		content = (
			<ErrorToastContent
				icon="🚨"
				title="Erro no Servidor"
				message="Nosso servidor está com problemas. Tente novamente em alguns minutos"
				actionButton={
					<div>
						{createActionButton("Recarregar Página", () => window.location.reload())}
						{createActionButton("Contatar Suporte", () => window.open('/support', '_blank'), false)}
					</div>
				}
			/>
		);
		toastId = 'server-error';
		toastOptions = getToastOptions(12000); // 12 segundos
	}
	// Erro de validação (400)
	else if (statusCode === 400) {
		const translatedMsg = errorMsg && i18n.exists(`backendErrors.${errorMsg}`) 
			? i18n.t(`backendErrors.${errorMsg}`) 
			: errorMsg || "Verifique os dados informados e tente novamente";

		content = (
			<ErrorToastContent
				icon="⚠️"
				title="Dados Inválidos"
				message={translatedMsg}
			/>
		);
		toastId = errorMsg || 'validation-error';
		toastOptions = getToastOptions(7000);
	}
	// Erro específico do backend
	else if (errorMsg) {
		const translatedMsg = i18n.exists(`backendErrors.${errorMsg}`) 
			? i18n.t(`backendErrors.${errorMsg}`) 
			: errorMsg;

		content = (
			<ErrorToastContent
				icon="❌"
				title="Erro"
				message={translatedMsg}
			/>
		);
		toastId = errorMsg;
		toastOptions = getToastOptions(8000);
	}
	// String simples
	else if (isString(err)) {
		content = (
			<ErrorToastContent
				icon="⚠️"
				message={err}
			/>
		);
		toastId = err.substring(0, 50); // Usar primeiros 50 chars como ID
		toastOptions = getToastOptions(6000);
	}
	// Erro desconhecido
	else {
		content = (
			<ErrorToastContent
				icon="❓"
				title="Ops! Algo deu errado"
				message="Ocorreu um erro inesperado. Se o problema persistir, entre em contato conosco"
				actionButton={
					<div>
						{createActionButton("Tentar Novamente", () => window.location.reload())}
						{createActionButton("Contatar Suporte", () => window.open('/support', '_blank'), false)}
					</div>
				}
			/>
		);
		toastId = 'unknown-error';
		toastOptions = getToastOptions(10000);
	}

	// Prevenir toasts duplicados
	if (toast.isActive(toastId)) {
		return;
	}

		// Mostrar toast com conteúdo apropriado
		toast.error(content, { ...toastOptions, toastId });
};

// Substitui globalmente toast.error para padronizar erros, inclusive quando passarem objetos/axios error
export const replaceToastError = () => {
	const originalToastError = toast.error;
	toast.error = (message, options = {}) => {
		// Se já for um elemento React, mantém o original
		if (React.isValidElement(message)) {
			return originalToastError(message, options);
		}
		// Padroniza quaisquer valores (string, objeto de erro, AxiosError, etc.)
		try {
			return toastError(message);
		} catch (_) {
			// Fallback inofensivo se algo der errado no parser custom
			return originalToastError(String(message || 'Erro desconhecido'), {
				position: 'top-right',
				theme: getCurrentToastTheme(),
				...options,
			});
		}
	};
	try { console.log('✅ Toast Error padronizado globalmente'); } catch {}
};

// Opcional: padroniza warning e info para manter tema/posição consistentes
export const replaceToastWarnInfo = () => {
	const theme = getCurrentToastTheme();
	const originalWarn = toast.warning;
	const originalWarnAlias = toast.warn;
	const originalInfo = toast.info;

	toast.warning = (message, options = {}) => {
		// Dedupe simples por mensagem (se string)
		const tid = isString(message) ? `warn:${message.slice(0, 60)}` : undefined;
		if (tid && toast.isActive(tid)) return;
		return originalWarn(message, { position: 'top-right', theme, toastId: tid, ...options });
	};

	// Garantir que toast.warn (alias comum) também fique padronizado
	toast.warn = (message, options = {}) => {
		const tid = isString(message) ? `warn:${message.slice(0, 60)}` : undefined;
		if (tid && toast.isActive(tid)) return;
		// Se originalWarnAlias existir, usa ele; senão, cai no originalWarn
		const base = originalWarnAlias || originalWarn;
		return base(message, { position: 'top-right', theme, toastId: tid, ...options });
	};

	toast.info = (message, options = {}) => {
		const tid = isString(message) ? `info:${message.slice(0, 60)}` : undefined;
		if (tid && toast.isActive(tid)) return;
		return originalInfo(message, { position: 'top-right', theme, toastId: tid, ...options });
	};
	try { console.log('✅ Toast Warning/Info padronizados globalmente'); } catch {}
};

export default toastError;
